import { useToast } from '@chakra-ui/react';
import { useContactFormMutation } from 'src/api/mutations';
import {
  PublicFormErrorReason,
  classifyPublicFormError,
} from 'src/utils/publicFormErrors';

type ContactFormHookData = {
  name: string;
  email: string;
  company: string;
  message: string;
  captchaToken: string;
  honeypot: string;
};

type SubmitResult =
  | { success: true }
  | { success: false; reason: PublicFormErrorReason };

const errorToastCopy: Record<
  PublicFormErrorReason,
  { title: string; description: string; status: 'warning' | 'error' }
> = {
  rate_limited: {
    title: 'Too many requests',
    description:
      'Please try again in a few minutes or contact us directly at support@yapamar.com',
    status: 'warning',
  },
  invalid_data: {
    title: 'Invalid form data',
    description: 'Please check your entries and try again.',
    status: 'error',
  },
  verification_failed: {
    title: 'Verification failed',
    description: 'Please complete the verification and try again.',
    status: 'error',
  },
  unknown: {
    title: 'Error sending message',
    description:
      'Please try again or contact us directly at support@yapamar.com',
    status: 'error',
  },
};

export const useContactForm = () => {
  const toast = useToast();
  const contactMutation = useContactFormMutation();

  const submitContactForm = async (
    data: ContactFormHookData,
  ): Promise<SubmitResult> => {
    try {
      await contactMutation.mutateAsync({
        formData: {
          firstName: data.name.split(' ')[0] || data.name,
          lastName: data.name.split(' ').slice(1).join(' ') || '',
          email: data.email,
          company: data.company,
          phone: '',
          message: data.message,
          interest: 'signup',
        },
        captchaToken: data.captchaToken,
        honeypot: data.honeypot,
      });

      toast({
        title: 'Message sent successfully!',
        description: "We'll get back to you within 24 hours.",
        status: 'success',
        isClosable: true,
        variant: 'solid',
      });
      return { success: true };
    } catch (error) {
      const reason = classifyPublicFormError(error);

      toast({
        ...errorToastCopy[reason],
        isClosable: true,
        variant: 'solid',
      });
      return { success: false, reason };
    }
  };

  return {
    submitContactForm,
    isLoading: contactMutation.isLoading,
    isError: contactMutation.isError,
    error: contactMutation.error,
  };
};
